// Unified PO receiving — one lean form for PMs (Purchasing grid, WO PO card) and Warehouse (phone).
// The material is in front of you: tap the PO, enter what's here, done. No prose, no status chatter.
// Posts to /procurement/pos/{id}/check-in: per-line "receiving now" qty, or Receive All for the remaining balance.
import { useEffect, useState } from "react";
import { CheckCircle2, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { btn } from "@/components/common/btn";
import { checkInProcurementPO } from "@/lib/api";

export const remainingQty = (li) => Math.max(0, (parseFloat(li.qty) || 0) - (parseFloat(li.received_qty) || 0));
export const openLines = (po) => (po.line_items || []).filter((li) => !li.received && remainingQty(li) > 0);

const BIG = "!h-10 !px-4 !text-sm";
// Why something didn't come in — tapping one flags the PO Partial even if nothing was checked in this time.
// "Other" is the catch-all and needs a note saying what happened.
const REASONS = ["Missing", "Damaged", "Other"];

function LineProgress({ li }) {
  const ordered = parseFloat(li.qty) || 0;
  const got = parseFloat(li.received_qty) || 0;
  if (!ordered || !got || li.received) return null;
  return (
    <div className="mt-1.5 h-1 w-full overflow-hidden rounded bg-slate-200">
      <div className="h-full bg-indigo-500" style={{ width: `${Math.min(100, (got / ordered) * 100)}%` }} />
    </div>
  );
}

export function ReceivePOForm({ po, onCheckedIn, testPrefix = "receive", big = false }) {
  const [qtys, setQtys] = useState({});
  const [reasons, setReasons] = useState([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const items = po.line_items || [];
  const key = po.po_number || po.id;
  const open = openLines(po);
  const size = big ? BIG : "";
  const notes = [...reasons, note.trim()].filter(Boolean).join(" · ");

  const submit = async (payload) => {
    setSaving(true);
    try {
      const { data } = await checkInProcurementPO(po.id, payload, notes, reasons);
      const full = data.status === "received";
      if (po.is_stock_po) {
        const hits = data.inventory?.hits || 0;
        toast.success(full ? `${po.po_number} received — On Hand updated (${hits})` : `Saved — On Hand updated (${hits})`);
        const misses = data.inventory?.misses || [];
        if (misses.length) toast.warning(`${misses.length} line(s) had no matching catalog part`);
      } else {
        toast.success(full ? `${po.po_number} received` : "Partial saved");
      }
      setQtys({}); setReasons([]); setNote("");
      onCheckedIn?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Check-in failed"); }
    finally { setSaving(false); }
  };

  const checkIn = () => {
    const payload = open.filter((li) => parseFloat(qtys[li.id]) > 0).map((li) => ({ line_item_id: li.id, qty: parseFloat(qtys[li.id]) }));
    if (!payload.length && !reasons.length) { toast.info("Enter a quantity, tap Receive All, or flag Missing / Damaged / Other."); return; }
    if (reasons.includes("Other") && !note.trim()) { toast.error("Add a note describing the issue for \"Other\"."); return; }
    submit(payload);
  };
  const receiveAll = () => submit(open.map((li) => ({ line_item_id: li.id, qty: null })));
  // One reason at a time — tapping the active chip clears it.
  const toggleReason = (r) => setReasons((cur) => (cur.includes(r) ? [] : [r]));

  return (
    <div className="space-y-3" data-testid={`${testPrefix}-po-body-${key}`}>
      <div className="space-y-1.5">
        {items.map((li) => {
          const rem = remainingQty(li);
          return (
            <div key={li.id} className={`rounded-md border px-3 py-2 ${li.received ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`} data-testid={`${testPrefix}-line-${li.id}`}>
              <div className="flex items-center gap-3">
                <div className="min-w-0 flex-1 truncate text-sm text-slate-800">
                  {li.part_number && <span className="mr-2 font-mono text-slate-500">{li.part_number}</span>}
                  {li.description || (!li.part_number && "—")}
                </div>
                {li.received ? (
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" aria-label="Received" data-testid={`${testPrefix}-line-done-${li.id}`} />
                ) : (
                  <div className="flex items-center gap-1.5 whitespace-nowrap">
                    <Input type="number" inputMode="decimal" min="0" step="any" placeholder="0" value={qtys[li.id] ?? ""}
                      onChange={(e) => setQtys((q) => ({ ...q, [li.id]: e.target.value }))}
                      className={`w-24 text-right text-sm [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${big ? "h-10" : "h-8"}`} data-testid={`${testPrefix}-qty-input-${li.id}`} />
                    <span className="text-xs text-slate-400 num">/ {rem}</span>
                    <button type="button" className="text-xs font-medium text-emerald-700 hover:underline" title={`Fill in the remaining ${rem}`}
                      onClick={() => setQtys((q) => ({ ...q, [li.id]: String(rem) }))} data-testid={`${testPrefix}-line-all-${li.id}`}>All</button>
                  </div>
                )}
              </div>
              <LineProgress li={li} />
            </div>
          );
        })}
      </div>
      {open.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid={`${testPrefix}-reasons-${key}`}>
          {REASONS.map((r) => {
            const on = reasons.includes(r);
            return (
              <button key={r} type="button" onClick={() => toggleReason(r)} aria-pressed={on} data-testid={`${testPrefix}-reason-${r.toLowerCase()}-${key}`}
                className={`rounded-full border px-2.5 text-xs font-medium transition-colors ${big ? "h-9" : "h-7"} ${on ? "border-amber-400 bg-amber-100 text-amber-800" : "border-slate-300 text-slate-600 hover:bg-slate-100"}`}>
                {r}
              </button>
            );
          })}
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={reasons.includes("Other") ? "What happened? (required)" : "Note…"}
            aria-required={reasons.includes("Other")} className={`min-w-[140px] flex-1 text-sm ${big ? "h-9" : "h-7"} ${reasons.includes("Other") && !note.trim() ? "border-amber-400" : ""}`} data-testid={`${testPrefix}-notes-${key}`} />
        </div>
      )}
      <div className="flex items-center justify-end gap-2">
        <button type="button" className={`${btn("mode")} ${size}`} onClick={receiveAll} disabled={saving || !open.length} title="Receive the remaining balance on every open line"
          data-testid={`${testPrefix}-receive-all-${key}`}>
          <PackageCheck className="h-3.5 w-3.5" /> Receive All
        </button>
        <button type="button" className={`${btn("write")} ${size}`} onClick={checkIn} disabled={saving || !open.length} data-testid={`${testPrefix}-checkin-btn-${key}`}>
          {saving ? "Saving…" : "Check In"}
        </button>
      </div>
    </div>
  );
}

// Keeps its own copy of the PO so a partial check-in refreshes the lines without closing; closes itself on full receipt.
export function ReceivePOModal({ po: initial, onClose, onCheckedIn, big = false }) {
  const [po, setPo] = useState(initial);
  useEffect(() => { setPo(initial); }, [initial]);
  if (!po) return null;
  const handle = (data) => {
    if (data?.po) setPo((p) => ({ ...p, ...data.po }));
    onCheckedIn?.(data);
    if (data?.status === "received") onClose();
  };
  // Header is just "PO#  WO name" (ABS Stock for warehouse restock) — the box is in front of you.
  const woName = po.is_stock_po ? "ABS Stock" : (po.wo_title || po.wo_number || "");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] w-[calc(100%-1rem)] max-w-2xl overflow-y-auto p-4 sm:p-6" data-testid={`receive-po-modal-${po.id}`}>
        <DialogHeader>
          <DialogTitle className="flex min-w-0 items-baseline gap-2 text-base">
            <span className="shrink-0 font-mono">{po.po_number}</span>
            {woName && <span className="truncate font-normal text-slate-700" data-testid={`receive-po-wo-${po.id}`}>{woName}</span>}
          </DialogTitle>
          <DialogDescription className="sr-only">Receive purchase order {po.po_number}</DialogDescription>
        </DialogHeader>
        <ReceivePOForm po={po} big={big} onCheckedIn={handle} />
      </DialogContent>
    </Dialog>
  );
}
